<style scoped>
  .message-center-root {
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    line-height: 0;
    vertical-align: middle;
  }

  .message-bell {
    position: relative;
    display: inline-flex;
    width: 36px;
    height: 36px;
    align-items: center;
    justify-content: center;
    margin: 0;
    box-sizing: border-box;
    border: 1px solid rgba(45, 140, 240, 0.3);
    border-radius: 8px;
    background: linear-gradient(145deg, rgba(45, 140, 240, 0.16), rgba(45, 140, 240, 0.05));
    color: #b7dcff;
    cursor: pointer;
    text-decoration: none;
    transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease;
  }

  .message-bell:hover {
    border-color: rgba(73, 175, 255, 0.72);
    background: linear-gradient(145deg, rgba(45, 140, 240, 0.3), rgba(45, 140, 240, 0.12));
    box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.08), 0 0 14px rgba(45, 140, 240, 0.25);
    color: #ffffff;
  }

  .message-bell:active {
    transform: translateY(1px);
  }

  .message-bell .ivu-icon {
    line-height: 1;
  }

  .message-bell-dot {
    position: absolute;
    top: 2px;
    right: 1px;
    min-width: 15px;
    height: 15px;
    padding: 0 3px;
    box-sizing: border-box;
    border: 1px solid #172b4d;
    border-radius: 8px;
    color: #ffffff;
    font-size: 9px;
    font-weight: 600;
    line-height: 13px;
    text-align: center;
    background: #ff5f6d;
    box-shadow: 0 0 7px rgba(255, 95, 109, 0.82);
  }

  .demo-badge{
    width: 32px;
    height: 32px;
    background: #eee;
    border-radius: 6px;
    display: inline-block;
  }
</style>
<template>
  <div class="message-center-root">
    <Dropdown trigger="custom" :visible="modal" transfer transfer-class-name="message-dropdown-transfer">
      <a class="message-bell" :title="lanMap.messageCenter[language]" @click="toMessage">
        <Icon type="ios-notifications-outline" size="23" />
        <span class="message-bell-dot" v-if="unreadCount > 0">{{ unreadCount > 99 ? '99+' : unreadCount }}</span>
      </a>
      <div slot="list" class="message-dropdown-menu">
        <div class="message-dropdown-header">
          <div class="message-dropdown-heading">
            <strong>{{ lanMap.messageCenter[language] }}</strong>
          </div>
          <div class="message-dropdown-actions">
            <Button
              type="text"
              size="small"
              class="message-readall-btn"
              :loading="readingAll"
              :disabled="unreadCount <= 0"
              @click="markAllRead"
            >{{ lanMap.notifyMarkAllRead[language] }}</Button>
            <Icon type="md-close" size="18" @click="closeMessageCenter"/>
          </div>
        </div>
        <div class="message-dropdown-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            :class="['message-tab', { 'is-active': activeTab === tab.key }]"
            @click="switchTab(tab.key)"
          >
            <span>{{ tab.label }}</span>
            <em v-if="tab.key === 'unread' && unreadCount > 0">{{ unreadCount > 99 ? '99+' : unreadCount }}</em>
          </button>
        </div>
        <div v-if="listLoading" class="message-dropdown-empty">
          <Icon type="ios-loading" size="26" class="message-loading-icon" />
        </div>
        <div v-else-if="list.length <= 0" class="message-dropdown-empty">
          <Icon type="ios-notifications-off-outline" size="30" />
          <span>{{ lanMap.empty[language] }}</span>
        </div>
        <div v-else class="message-dropdown-body">
          <div class="message-dropdown-scroll">
            <article
              v-for="(item, itemIndex) in list"
              :key="item.id || item.createTime || itemIndex"
              :class="[
                'message-item',
                item.notifyType === 'CAPACITY_ALERT' ? 'is-alert' : 'is-report',
                isRead(item) ? 'is-read' : 'is-unread'
              ]"
            >
              <div class="message-item-head">
                <i v-if="!isRead(item)" class="message-item-dot"></i>
                <span class="message-item-type">{{ item.notifyType === 'CAPACITY_ALERT' ? lanMap.notifyCapacityAlert[language] : lanMap.notifyCleanupReport[language] }}</span>
                <strong class="message-item-title" :title="item.title">{{ stripLeadingTime(item.title) || '--' }}</strong>
                <time class="message-item-time">{{ item.createTime || '--' }}</time>
              </div>
              <p class="message-item-summary">{{ item.summary || '--' }}</p>
              <div class="message-item-actions">
                <span v-if="isRead(item)" class="message-item-state">{{ lanMap.notifyRead[language] }}</span>
                <Button type="primary" size="small" ghost @click="openNotification(item)">{{ lanMap.details[language] }}</Button>
                <Button
                  v-if="!isRead(item)"
                  size="small"
                  :loading="readingId === item.id"
                  @click="markRead(item)"
                >{{ lanMap.notifyMarkRead[language] }}</Button>
              </div>
            </article>
          </div>
          <div class="message-dropdown-pagination">
            <Page :current="index" :total="total" :page-size="10" @on-change="goPageData"></Page>
          </div>
        </div>
      </div>
    </Dropdown>

    <Modal v-model="detailVisible" width="640" class-name="notification-detail-modal" :mask-closable="false">
      <div slot="header" class="notification-detail-title">
        <div class="notification-detail-heading">
          <span :class="['notification-type-tag', notificationTypeClass]">{{ notificationTypeText }}</span>
          <strong :title="detail.title">{{ stripLeadingTime(detail.title) || '--' }}</strong>
        </div>
        <small>{{ detail.createTime || '--' }}</small>
      </div>
      <Spin fix v-if="detailLoading"></Spin>
      <div v-if="!detailLoading" class="notification-detail-content">
        <p v-if="detail.summary" class="notification-detail-summary">{{ detail.summary }}</p>

        <template v-if="report">
          <div class="notification-metric-grid">
            <div class="notification-metric is-records">
              <span><Icon type="ios-filing-outline" />{{ lanMap.notifyDeletedRecords[language] }}</span>
              <strong :class="{ 'is-zero': isZero(report.totalDeleted) }">{{ formatNumber(report.totalDeleted) }}</strong>
            </div>
            <div class="notification-metric is-freed">
              <span><Icon type="ios-cloud-download-outline" />{{ lanMap.notifyFreedSpace[language] }}</span>
              <strong :class="{ 'is-zero': isZero(report.freedBytes) }">{{ formatBytes(report.freedBytes) }}</strong>
            </div>
            <div class="notification-metric is-tables">
              <span><Icon type="ios-grid-outline" />{{ lanMap.notifyInvolvedTables[language] }}</span>
              <strong :class="{ 'is-zero': isZero(report.totalTables) }">{{ formatNumber(report.totalTables) }}</strong>
            </div>
            <div class="notification-metric is-duration">
              <span><Icon type="ios-timer-outline" />{{ lanMap.notifyDuration[language] }}</span>
              <strong>{{ formatDuration(report.durationMs) }}</strong>
            </div>
          </div>

          <section class="notification-section is-disk">
            <div class="notification-section-title">
              <span><Icon type="ios-speedometer-outline" />{{ lanMap.notifyDiskSection[language] }}</span>
              <em :class="diskLevelClass">{{ lanMap.notifyDiskUsage[language] }} {{ diskUsageText }}</em>
            </div>
            <div class="notification-disk-track">
              <i :class="diskLevelClass" :style="{ width: diskUsageWidth }"></i>
            </div>
            <div class="notification-kv-grid">
              <div><span>{{ lanMap.notifyDiskBefore[language] }}</span><strong>{{ formatMegabytes(report.diskBeforeMb) }}</strong></div>
              <div><span>{{ lanMap.notifyDiskAfter[language] }}</span><strong>{{ formatMegabytes(report.diskAfterMb) }}</strong></div>
              <div><span>{{ lanMap.notifyFileFreed[language] }}</span><strong>{{ formatBytes(report.freedByFiles) }}</strong></div>
            </div>
          </section>

          <section class="notification-section is-execution">
            <div class="notification-section-title">
              <span><Icon type="ios-play-outline" />{{ lanMap.notifyExecutionSection[language] }}</span>
              <em v-if="report.taskId" class="notification-task-id" :title="report.taskId">{{ lanMap.taskId[language] }} · {{ report.taskId }}</em>
            </div>
            <div class="notification-kv-grid">
              <div>
                <span>{{ lanMap.notifyTrigger[language] }}</span>
                <em :class="['notification-trigger-tag', triggerClass]">{{ triggerText }}</em>
              </div>
              <div><span>{{ lanMap.notifyStartTime[language] }}</span><strong class="is-datetime">{{ formatTime(report.startTime) }}</strong></div>
              <div><span>{{ lanMap.notifyEndTime[language] }}</span><strong class="is-datetime">{{ formatTime(report.endTime) }}</strong></div>
              <div><span>{{ lanMap.notifyDeletedFiles[language] }}</span><strong :class="{ 'is-zero': isZero(report.deletedFiles) }">{{ formatNumber(report.deletedFiles) }}</strong></div>
              <div><span>{{ lanMap.notifyFileSize[language] }}</span><strong :class="{ 'is-zero': isZero(report.deletedFileBytes) }">{{ formatBytes(report.deletedFileBytes) }}</strong></div>
            </div>
          </section>

          <section class="notification-section is-capacity">
            <div class="notification-section-title"><span><Icon type="ios-build-outline" />{{ lanMap.notifyCapacitySection[language] }}</span></div>
            <div class="notification-flag-row">
              <div :class="['notification-flag', report.phase2Executed ? 'is-on' : 'is-off']">
                <i><Icon :type="report.phase2Executed ? 'md-checkmark' : 'md-remove'" /></i>
                <div>
                  <span>{{ lanMap.notifyCapacityGuard[language] }}</span>
                  <strong>{{ report.phase2Executed ? lanMap.notifyTriggered[language] : lanMap.notifyNotTriggered[language] }}</strong>
                </div>
              </div>
              <div :class="['notification-flag', report.capacityAlert ? 'is-alert' : 'is-safe']">
                <i><Icon :type="report.capacityAlert ? 'md-alert' : 'md-checkmark'" /></i>
                <div>
                  <span>{{ lanMap.notifyCapacityAlert[language] }}</span>
                  <strong>{{ report.capacityAlert ? lanMap.notifyTriggered[language] : lanMap.notifyNotTriggered[language] }}</strong>
                </div>
              </div>
              <div :class="['notification-flag', report.optimizeExecuted ? 'is-on' : 'is-off']">
                <i><Icon :type="report.optimizeExecuted ? 'md-checkmark' : 'md-remove'" /></i>
                <div>
                  <span>OPTIMIZE</span>
                  <strong>{{ report.optimizeExecuted ? lanMap.notifyExecuted[language] : lanMap.notifyNotExecuted[language] }}</strong>
                </div>
              </div>
            </div>
            <div class="notification-kv-grid">
              <div><span>{{ lanMap.notifyOptimizeTables[language] }}</span><strong :class="{ 'is-zero': isZero(report.optimizeTables) }">{{ formatNumber(report.optimizeTables) }}</strong></div>
              <div><span>{{ lanMap.notifyOptimizeFreed[language] }}</span><strong :class="{ 'is-zero': isZero(report.freedByOptimize) }">{{ formatBytes(report.freedByOptimize) }}</strong></div>
            </div>
          </section>
        </template>

        <pre v-else-if="detailContentText" class="notification-detail-json">{{ detailContentText }}</pre>
        <div v-else-if="!detail.summary" class="notification-detail-empty">
          <Icon type="ios-information-circle-outline" size="26" />
          <span>{{ lanMap.empty[language] }}</span>
        </div>
      </div>
      <div slot="footer">
        <Button
          v-if="detail.id && !isRead(detail)"
          type="primary"
          :loading="readingId === detail.id"
          @click="markRead(detail, true)"
        >{{ lanMap.notifyMarkRead[language] }}</Button>
        <Button v-else @click="detailVisible = false">{{ lanMap.close[language] }}</Button>
      </div>
    </Modal>

    <!--<Modal
      v-model="modal"
      title="消息中心" width="760">
      <div v-if="list.length <= 0" style="width: 100%;text-align: center">暂时没有数据</div>
      <Collapse accordion v-if="list.length > 0">
        <Panel :name="index + ''" v-for="(item, index) in list">
          {{item.title}}-[{{item.createTime}}]
          <p slot="content">{{item.content}}</p>
        </Panel>
      </Collapse>

      &lt;!&ndash;分页数据&ndash;&gt;
      <div style="text-align:center;padding-top:10px;" v-if="list.length>0">
        <Page :current="index" :total="total" :page-size="15" @on-change="goPageData"></Page>
      </div>
      <div slot="footer"></div>
    </Modal>-->

  </div>
</template>

<script>
import lanMap from '@/utils/language';
import { mapState } from 'vuex';
import api from "../../../../api/index.js";

export default {
  name: 'Language',
  data () {
    return {
      lanMap:lanMap,
      index: 1,
      total: 0,
      list:[],
      unreadCount: 0,
      // 消息页签：unread 未读 / read 已读 / all 全部
      activeTab: 'unread',
      listLoading: false,
      modal:false,
      detailVisible: false,
      detailLoading: false,
      detail: {},
      readingId: 0,
      readingAll: false,
      refreshHandler: null,
      langList: {
        'zh-CN': '语言',
        'zh-TW': '語言',
        'en-US': 'Lang'
      },
      localList: {
        'zh-CN': '中文简体',
        'zh-TW': '中文繁体',
        'en-US': 'English'
      }
    }
  },
  computed: {
    ...mapState({
      language: state => state.app.language,
    }),
    title () {
      return this.langList[this.lang]
    },
    tabs () {
      return [
        { key: 'unread', label: this.lanMap.notifyUnread[this.language] },
        { key: 'read', label: this.lanMap.notifyRead[this.language] },
        { key: 'all', label: this.lanMap.all[this.language] }
      ]
    },
    detailContentText () {
      const content = this.detail && this.detail.contentJson
      if (!content) return ''
      try {
        return JSON.stringify(JSON.parse(content), null, 2)
      } catch (error) {
        return content
      }
    },
    report () {
      const content = this.detail && this.detail.contentJson
      if (!content) return null
      try {
        const parsed = JSON.parse(content)
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
      } catch (error) {
        return null
      }
    },
    notificationTypeText () {
      return this.detail.notifyType === 'CAPACITY_ALERT'
        ? this.lanMap.notifyCapacityAlert[this.language]
        : this.lanMap.notifyCleanupReport[this.language]
    },
    notificationTypeClass () {
      return this.detail.notifyType === 'CAPACITY_ALERT' ? 'is-alert' : 'is-report'
    },
    triggerText () {
      const map = {
        SCHEDULED: this.lanMap.notifyTriggerScheduled[this.language],
        THRESHOLD: this.lanMap.notifyTriggerThreshold[this.language],
        MANUAL: this.lanMap.notifyTriggerManual[this.language]
      }
      const type = this.report && this.report.triggerType
      return map[type] || type || '--'
    },
    triggerClass () {
      const map = { SCHEDULED: 'is-scheduled', THRESHOLD: 'is-threshold', MANUAL: 'is-manual' }
      return map[this.report && this.report.triggerType] || 'is-scheduled'
    },
    diskUsagePercent () {
      const percent = Number(this.report && this.report.diskUsagePercent)
      if (!Number.isFinite(percent)) return null
      return Math.min(100, Math.max(0, percent))
    },
    diskUsageText () {
      return this.diskUsagePercent === null ? '--' : this.diskUsagePercent.toFixed(1) + '%'
    },
    diskUsageWidth () {
      return (this.diskUsagePercent === null ? 0 : this.diskUsagePercent) + '%'
    },
    diskLevelClass () {
      if (this.diskUsagePercent === null) return 'is-normal'
      if (this.diskUsagePercent >= 90) return 'is-danger'
      if (this.diskUsagePercent >= 75) return 'is-warning'
      return 'is-normal'
    }
  },
  created () {
    this.getUnreadCount()
  },
  mounted () {
    // 收到容量告警等实时推送后由 main.vue 触发，用于同步未读数
    this.refreshHandler = () => this.getUnreadCount()
    document.body.addEventListener('notification-refresh', this.refreshHandler)
  },
  beforeDestroy () {
    if (this.refreshHandler) document.body.removeEventListener('notification-refresh', this.refreshHandler)
  },
  methods: {
    toMessage () {
      if (this.modal) {
        this.closeMessageCenter()
        return
      }
      this.activeTab = 'unread'
      this.index = 1
      this.modal = true
      this.getUnreadCount().then(() => this.getList())
    },
    switchTab (tab) {
      if (this.activeTab === tab || this.listLoading) return
      this.activeTab = tab
      this.index = 1
      this.getList()
    },
    getUnreadCount () {
      return api.getData('system-server/notification/unread-count', this).then(res => {
        if (res.state !== 0) this.unreadCount = Number(res.data || 0)
      })
    },
    // 三个页签统一走历史接口，由 readFlag 过滤，保证服务端分页准确
    getList () {
      this.list = []
      this.listLoading = true
      const readFlagQuery = { unread: '&readFlag=0', read: '&readFlag=1', all: '' }[this.activeTab] || ''
      const url = 'system-server/notification/history?index=' + this.index + '&pageSize=10' + readFlagQuery
      return api.getData(url, this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({ title: this.lanMap.tips[this.language], desc: res.msg })
          return
        }
        const data = res.data || {}
        const rows = Array.isArray(data) ? data : (Array.isArray(data.rows) ? data.rows : [])
        this.list = rows
        if (!Array.isArray(data)) {
          this.total = Number(data.total || rows.length)
        } else {
          // 接口直接返回数组时没有总数，未读页签用未读数兜底
          this.total = this.activeTab === 'unread' ? Math.max(this.unreadCount, rows.length) : rows.length
        }
      }).finally(() => { this.listLoading = false })
    },
    closeMessageCenter () {
      this.modal = false
    },
    openNotification (notification) {
      if (!notification || !notification.id) return
      this.detail = {}
      this.detailVisible = true
      this.detailLoading = true
      api.getData('system-server/notification/' + notification.id + '/detail', this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({ title: this.lanMap.tips[this.language], desc: res.msg })
          this.detailVisible = false
          return
        }
        // 详情可能不带 readFlag，用列表行兜底，保证底部按钮状态正确
        this.detail = Object.assign({}, notification, res.data || {})
      }).finally(() => { this.detailLoading = false })
    },
    markRead (notification, fromDetail) {
      if (!notification || !notification.id || this.readingId) return
      this.readingId = notification.id
      api.postData('system-server/notification/' + notification.id + '/read', {}, this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({ title: this.lanMap.tips[this.language], desc: res.msg })
          return
        }
        if (fromDetail) this.detailVisible = false
        return this.reloadAfterRead()
      }).finally(() => { this.readingId = 0 })
    },
    markAllRead () {
      if (this.readingAll || this.unreadCount <= 0) return
      this.readingAll = true
      api.postData('system-server/notification/read-all', {}, this).then(res => {
        if (res.state === 0) {
          this.$Notice.error({ title: this.lanMap.tips[this.language], desc: res.msg })
          return
        }
        this.index = 1
        this.unreadCount = 0
        return this.getList()
      }).finally(() => { this.readingAll = false })
    },
    // 标记已读后当前页数据会变，刷新未读数并回收可能变空的当前页
    reloadAfterRead () {
      return this.getUnreadCount()
        .then(() => this.getList())
        .then(() => {
          if (this.list.length === 0 && this.index > 1) {
            this.index -= 1
            return this.getList()
          }
        })
    },
    goPageData (index) {
      this.index = index
      this.getList()
    },
    isRead (item) {
      return Number(item && item.readFlag) === 1
    },
    isZero (value) {
      return Number(value || 0) === 0
    },
    // 通知标题常以日期时间开头，与单独展示的创建时间重复，展示时去掉该前缀
    stripLeadingTime (title) {
      if (!title) return ''
      const stripped = String(title).replace(/^\d{4}-\d{2}-\d{2}(\s+\d{2}:\d{2}(:\d{2})?)?\s*/, '')
      return stripped || String(title)
    },
    formatNumber (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toLocaleString() : '0'
    },
    formatBytes (value) {
      const bytes = Number(value || 0)
      if (!Number.isFinite(bytes) || bytes <= 0) return '0 B'
      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
      const amount = bytes / Math.pow(1024, index)
      return (amount >= 10 || index === 0 ? amount.toFixed(0) : amount.toFixed(1)) + ' ' + units[index]
    },
    formatMegabytes (value) {
      const number = Number(value)
      return Number.isFinite(number) ? this.formatNumber(number) + ' MB' : '--'
    },
    formatDuration (value) {
      const milliseconds = Number(value)
      if (!Number.isFinite(milliseconds) || milliseconds < 0) return '--'
      if (milliseconds < 1000) return milliseconds + ' ms'
      if (milliseconds < 60000) return (milliseconds / 1000).toFixed(1) + ' s'
      return Math.floor(milliseconds / 60000) + ' m ' + Math.floor((milliseconds % 60000) / 1000) + ' s'
    },
    formatTime (value) {
      if (value === null || value === undefined || value === '') return '--'
      if (typeof value !== 'number' && !/^\d+$/.test(String(value))) return String(value)
      const date = new Date(Number(value))
      if (Number.isNaN(date.getTime())) return String(value)
      const pad = number => String(number).padStart(2, '0')
      return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()) +
        ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds())
    }
  }
}
</script>


<style lang="less">
.ivu-select-dropdown.message-dropdown-transfer {
  position: fixed !important;
  top: 68px !important;
  right: 16px !important;
  left: auto !important;
  /* 去掉 iView 下拉默认的 5px 内外边距和白底外壳，只保留内部面板自身的外观 */
  margin: 0 !important;
  padding: 0 !important;
  overflow: visible !important;
  max-height: none !important;
  background: transparent !important;
  box-shadow: none !important;
  z-index: 2001 !important;
}
.ivu-select-dropdown.message-dropdown-transfer .message-dropdown-menu {
  width: 560px;
  box-sizing: border-box;
  padding: 0;
  overflow: hidden;
  max-height: 520px;
  list-style: none;
  border: 1px solid rgba(45, 140, 240, 0.32);
  border-radius: 10px;
  background: #f8fbff;
  box-shadow: 0 12px 30px rgba(21, 56, 96, 0.24), 0 0 0 1px rgba(45, 140, 240, 0.06);
}
.message-dropdown-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 18px;
  border-bottom: 1px solid rgba(45, 140, 240, 0.14);
  background: linear-gradient(135deg, #eef7ff, #ffffff);
  color: #243b5a;
}
.message-dropdown-header > .ivu-icon { color: #7e94ad; cursor: pointer; transition: color .2s ease, transform .2s ease; }
.message-dropdown-actions > .ivu-icon { color: #7e94ad; cursor: pointer; transition: color .2s ease, transform .2s ease; }
.message-dropdown-actions > .ivu-icon:hover { color: #2d8cf0; transform: rotate(90deg); }
.message-dropdown-header strong { color: #29476b; font-size: 15px; font-weight: 600; line-height: 1; }
.message-dropdown-heading { display: flex; align-items: center; gap: 8px; }
.message-dropdown-heading em { padding: 1px 8px; border-radius: 10px; background: #eaf3ff; color: #1e40af; font-size: 11px; font-style: normal; font-weight: 600; line-height: 18px; }
.message-dropdown-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 12px; }
.message-readall-btn.ivu-btn { height: 26px; padding: 0 8px; color: #2d8cf0; font-size: 12px; }
.message-readall-btn.ivu-btn:hover { background: #eaf3ff; }
.message-readall-btn.ivu-btn[disabled] { color: #a9bcd0; background: transparent; }
.message-dropdown-body { padding: 8px 16px; }
.message-dropdown-scroll { max-height: 360px; padding: 0 4px 0 0; overflow-y: auto; scrollbar-color: #b8d8f7 #eef5fc; scrollbar-width: thin; }
.message-dropdown-scroll::-webkit-scrollbar { width: 6px; }
.message-dropdown-scroll::-webkit-scrollbar-track { border-radius: 3px; background: #eef5fc; }
.message-dropdown-scroll::-webkit-scrollbar-thumb { border-radius: 3px; background: #b5d5f2; }
.message-item { margin-bottom: 8px; padding: 10px 12px; border: 1px solid #e0ebf6; border-left: 3px solid #cbd9e8; border-radius: 8px; background: #ffffff; transition: border-color .2s ease, box-shadow .2s ease; }
.message-item:last-child { margin-bottom: 0; }
.message-item:hover { border-color: #b9d6f5; border-left-color: #2d8cf0; box-shadow: 0 2px 8px rgba(45, 140, 240, 0.1); }
.message-item.is-report { border-left-color: #2d8cf0; }
.message-item.is-alert { border-left-color: #d97706; }
.message-item-head { display: flex; align-items: center; gap: 8px; }
.message-item-type { flex: 0 0 auto; padding: 1px 7px; border-radius: 4px; font-size: 11px; line-height: 18px; }
.message-item.is-report .message-item-type { background: #eaf3ff; color: #1e40af; }
.message-item.is-alert .message-item-type { background: #fff5e8; color: #9a5b06; }
.message-item-title { flex: 1; min-width: 0; overflow: hidden; color: #1f3f63; font-size: 13px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.message-item-time { flex: 0 0 auto; color: #5b7593; font-size: 11px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.message-item-summary { display: -webkit-box; overflow: hidden; margin: 6px 0 0; color: #5f7d9a; font-size: 12px; line-height: 1.6; word-break: break-word; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.message-item-actions { display: flex; align-items: center; justify-content: flex-end; gap: 8px; margin-top: 8px; }
.message-item-actions .ivu-btn { height: 26px; padding: 0 10px; border-radius: 5px; font-size: 12px; }

/* 消息页签 */
.message-dropdown-tabs { display: flex; align-items: center; gap: 4px; padding: 10px 16px 0; background: #f8fbff; }
.message-tab { display: inline-flex; height: 28px; align-items: center; gap: 6px; padding: 0 14px; border: 1px solid transparent; border-radius: 14px; background: transparent; color: #5b7593; cursor: pointer; font-size: 12px; outline: none; transition: background .2s ease, color .2s ease, border-color .2s ease; }
.message-tab:hover { background: #eaf3ff; color: #2d8cf0; }
.message-tab.is-active { border-color: rgba(45, 140, 240, 0.32); background: #eaf3ff; color: #1e40af; font-weight: 600; }
.message-tab em { min-width: 16px; padding: 0 5px; border-radius: 8px; background: #ff5f6d; color: #ffffff; font-size: 10px; font-style: normal; font-weight: 600; line-height: 16px; text-align: center; }
.message-loading-icon { color: #2d8cf0; animation: message-loading-spin 1s linear infinite; }
@keyframes message-loading-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

/* 已读消息弱化展示，与未读拉开层级 */
.message-item-dot { width: 6px; height: 6px; flex: 0 0 auto; border-radius: 50%; background: #2d8cf0; box-shadow: 0 0 0 3px rgba(45, 140, 240, 0.12); }
.message-item.is-alert .message-item-dot { background: #d97706; box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.12); }
.message-item.is-read { border-left-color: #dbe4ee; background: #fbfcfe; }
.message-item.is-read:hover { border-left-color: #b9d6f5; }
.message-item.is-read .message-item-type { background: #eef2f7; color: #7b90a6; }
.message-item.is-read .message-item-title { color: #5b7593; font-weight: 500; }
.message-item.is-read .message-item-summary { color: #8fa3b8; }
.message-item-state { margin-right: auto; padding: 0 8px; border-radius: 4px; background: #eef2f7; color: #7b90a6; font-size: 11px; line-height: 20px; }

.message-dropdown-pagination { padding-top: 6px; text-align: center; }
.message-dropdown-pagination .ivu-page-item, .message-dropdown-pagination .ivu-page-prev, .message-dropdown-pagination .ivu-page-next { border-color: #d6e6f5; background: #ffffff; }
.message-dropdown-pagination .ivu-page-item a, .message-dropdown-pagination .ivu-page-prev a, .message-dropdown-pagination .ivu-page-next a { color: #5b7a9a; }
.message-dropdown-pagination .ivu-page-item-active { border-color: #2d8cf0; background: #2d8cf0; }
.message-dropdown-pagination .ivu-page-item-active a { color: #ffffff; }
.message-dropdown-empty { display: flex; min-height: 180px; align-items: center; justify-content: center; flex-direction: column; gap: 10px; color: #8da2b8; font-size: 12px; }
.message-dropdown-empty .ivu-icon { color: #9bc6ed; }

/* 通知详情：数据密集型仪表盘布局 */
.notification-detail-modal .ivu-modal-body { padding: 16px 20px; background: #f8fbff; }
.notification-detail-modal .ivu-modal-header { padding: 12px 20px; border-bottom: 1px solid #dbeafe; }
.notification-detail-title { display: flex; align-items: center; justify-content: space-between; gap: 16px; padding-right: 30px; }
.notification-detail-heading { display: flex; min-width: 0; align-items: center; gap: 8px; }
.notification-detail-heading strong { overflow: hidden; color: #1f3f63; font-size: 15px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.notification-detail-title small { flex: 0 0 auto; color: #5b7593; font-size: 12px; font-variant-numeric: tabular-nums; }
.notification-type-tag { flex: 0 0 auto; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500; line-height: 18px; }
.notification-type-tag.is-report { border: 1px solid #dbeafe; background: #eaf3ff; color: #1e40af; }
.notification-type-tag.is-alert { border: 1px solid #fde3c2; background: #fff5e8; color: #9a5b06; }
.notification-detail-content { min-height: 140px; }
.notification-detail-summary { margin: 0 0 12px; color: #42617f; font-size: 13px; line-height: 1.7; }
.notification-metric-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
.notification-metric { position: relative; min-width: 0; padding: 10px 12px; overflow: hidden; border: 1px solid #e0ebf6; border-radius: 8px; background: #ffffff; }
.notification-metric:before { position: absolute; top: 0; bottom: 0; left: 0; width: 3px; content: ''; }
.notification-metric span { display: flex; align-items: center; gap: 5px; overflow: hidden; color: #5b7593; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.notification-metric span .ivu-icon { flex: 0 0 auto; font-size: 13px; }
.notification-metric strong { display: block; margin-top: 5px; overflow: hidden; font-size: 17px; font-weight: 600; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
.notification-metric strong.is-zero { color: #9db0c4 !important; font-weight: 500; }
.notification-metric.is-records:before { background: #2d8cf0; }
.notification-metric.is-records span .ivu-icon, .notification-metric.is-records strong { color: #1e5fae; }
.notification-metric.is-freed:before { background: #14a06a; }
.notification-metric.is-freed span .ivu-icon, .notification-metric.is-freed strong { color: #14804a; }
.notification-metric.is-tables:before { background: #7c4ddb; }
.notification-metric.is-tables span .ivu-icon, .notification-metric.is-tables strong { color: #5b32ac; }
.notification-metric.is-duration:before { background: #d97706; }
.notification-metric.is-duration span .ivu-icon, .notification-metric.is-duration strong { color: #9a5b06; }
.notification-section { margin-bottom: 12px; padding: 12px 12px 12px 14px; border: 1px solid #e0ebf6; border-left: 3px solid #cbd9e8; border-radius: 8px; background: #ffffff; }
.notification-section:last-child { margin-bottom: 0; }
.notification-section.is-disk { border-left-color: #2d8cf0; }
.notification-section.is-execution { border-left-color: #7c4ddb; }
.notification-section.is-capacity { border-left-color: #d97706; }
.notification-section-title { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
.notification-section-title span { display: flex; align-items: center; gap: 6px; color: #1f3f63; font-size: 13px; font-weight: 600; }
.notification-section-title span .ivu-icon { font-size: 15px; }
.notification-section.is-disk .notification-section-title span .ivu-icon { color: #2d8cf0; }
.notification-section.is-execution .notification-section-title span .ivu-icon { color: #7c4ddb; }
.notification-section.is-capacity .notification-section-title span .ivu-icon { color: #d97706; }
.notification-section-title em { flex: 0 0 auto; padding: 2px 8px; border-radius: 10px; font-size: 12px; font-style: normal; font-weight: 600; font-variant-numeric: tabular-nums; }
.notification-section-title em.is-normal { background: #eaf3ff; color: #1e40af; }
.notification-section-title em.is-warning { background: #fff5e8; color: #9a5b06; }
.notification-section-title em.is-danger { background: #fdecec; color: #c02626; }
.notification-section-title em.notification-task-id { flex: 0 1 auto; min-width: 0; overflow: hidden; background: #f4f8fd; color: #40628a; font-family: Consolas, 'Courier New', monospace; font-size: 11px; font-weight: 500; text-overflow: ellipsis; white-space: nowrap; }
.notification-disk-track { height: 8px; margin-bottom: 12px; overflow: hidden; border-radius: 4px; background: #e9eef6; }
.notification-disk-track i { display: block; height: 100%; border-radius: 4px; transition: width .3s ease; }
.notification-disk-track i.is-normal { background: #2d8cf0; }
.notification-disk-track i.is-warning { background: #d97706; }
.notification-disk-track i.is-danger { background: repeating-linear-gradient(135deg, #dc2626, #dc2626 5px, #e85252 5px, #e85252 10px); }
.notification-kv-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 12px; }
.notification-kv-grid > div { min-width: 0; }
.notification-kv-grid span { display: block; color: #5b7593; font-size: 11px; }
.notification-kv-grid strong { display: block; margin-top: 3px; overflow: hidden; color: #26456a; font-size: 13px; font-weight: 500; font-variant-numeric: tabular-nums; text-overflow: ellipsis; white-space: nowrap; }
.notification-kv-grid strong.is-zero { color: #9db0c4; }
.notification-kv-grid strong.is-datetime { font-size: 12px; letter-spacing: -0.1px; white-space: nowrap; }
.notification-kv-grid strong.is-code { padding: 2px 6px; border-radius: 4px; background: #f4f8fd; color: #40628a; font-family: Consolas, 'Courier New', monospace; font-size: 12px; }
.notification-trigger-tag { display: inline-block; margin-top: 3px; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-style: normal; font-weight: 500; }
.notification-trigger-tag.is-scheduled { background: #eaf3ff; color: #1e40af; }
.notification-trigger-tag.is-threshold { background: #fff5e8; color: #9a5b06; }
.notification-trigger-tag.is-manual { background: #f3edff; color: #5b32ac; }
.notification-flag-row { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin-bottom: 12px; }
.notification-flag { display: flex; min-width: 0; align-items: center; gap: 8px; padding: 8px 10px; border: 1px solid #e6eef7; border-radius: 8px; background: #fbfdff; }
.notification-flag > i { display: flex; width: 24px; height: 24px; flex: 0 0 auto; align-items: center; justify-content: center; border-radius: 50%; }
.notification-flag > i .ivu-icon { font-size: 15px; }
.notification-flag > div { min-width: 0; }
.notification-flag span { display: block; overflow: hidden; color: #5b7593; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.notification-flag strong { display: block; margin-top: 2px; overflow: hidden; font-size: 12px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.notification-flag.is-off > i { background: #eef3f9; color: #8296ad; }
.notification-flag.is-off strong { color: #6b8199; }
.notification-flag.is-on { border-color: #cdeadb; background: #f5fcf8; }
.notification-flag.is-on > i { background: #dff5ea; color: #14804a; }
.notification-flag.is-on strong { color: #14804a; }
.notification-flag.is-safe { border-color: #d5e6f8; background: #f6faff; }
.notification-flag.is-safe > i { background: #e3eefb; color: #1e5fae; }
.notification-flag.is-safe strong { color: #1e5fae; }
.notification-flag.is-alert { border-color: #f8cfcf; background: #fef7f7; }
.notification-flag.is-alert > i { background: #fbdede; color: #c02626; }
.notification-flag.is-alert strong { color: #c02626; }
.notification-detail-json { max-height: 280px; margin: 0; padding: 12px; overflow: auto; border: 1px solid #e0ebf6; border-radius: 8px; background: #ffffff; color: #4c6b8a; font: 12px/1.6 Consolas, 'Courier New', monospace; white-space: pre-wrap; word-break: break-word; }
.notification-detail-empty { display: flex; min-height: 140px; align-items: center; justify-content: center; flex-direction: column; gap: 8px; color: #5b7593; font-size: 13px; }
.notification-detail-empty .ivu-icon { color: #9bc6ed; }
@media (prefers-reduced-motion: reduce) {
  .notification-disk-track i { transition: none; }
}
</style>
